import PropTypes from 'prop-types';
import { useIntl } from 'gatsby-plugin-react-intl';
import ReactHtmlParser from 'react-html-parser';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';

import RichText from '../../../Core/RichText';
import BreadCrumbs from '../../BreadCrumbs';
import Link from '../../../Core/Link';
import ProTip from '../../ProTip';
import Chart from '../../Chart';
import TextWithIcon from '../../TextWithIcon';
import img from '../../../../images/video-icon.svg';

import {
  StyledContainer,
  NavBar,
  Title,
  Section,
  StyledParagraph,
  StyledFAQSearch,
  Subheading,
  Olist,
  Ulist,
  StyledLink,
} from './styles';

const answerFormat = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <StyledParagraph>{children}</StyledParagraph>
    ),
    [BLOCKS.OL_LIST]: (_, children) => <Olist>{children}</Olist>,
    [BLOCKS.UL_LIST]: (_, children) => <Ulist>{children}</Ulist>,
    [INLINES.HYPERLINK]: (node, children) => {
      const { data } = node;
      return (
        <Link className="rich-text-link" to={data.uri} fontSize="inherit">
          {children}
        </Link>
      );
    },
  },
};

const FAQAnswer = ({
  question,
  questionSubheading = '',
  answer = null,
  category,
  categorySlug,
  proTip = null,
  videoBlock = null,
  chart = null,
}) => {
  const intl = useIntl();
  return (
    <>
      <BreadCrumbs
        className="end-chevron"
        crumbs={[
          {
            label: `${intl.formatMessage({ id: 'learningHub' })}`,
            slug: '/learning-hub',
            active: true,
          },
          {
            label: `${intl.formatMessage({ id: 'FAQs' })}`,
            slug: '/learning-hub/faqs',
            active: true,
          },
          {
            label: ReactHtmlParser(category),
            slug: `/learning-hub/faqs/${categorySlug}`,
            active: true,
          },
        ]}
      />
      <StyledContainer>
        <NavBar>
          <StyledFAQSearch
            type="is-open"
            placeholder={`${intl.formatMessage({
              id: 'FAQSearchPlaceholder',
            })}`}
            predictiveSearch
            predictiveSearchClass="centered-results"
          />
        </NavBar>
        <Section>
          <Title>{ReactHtmlParser(question)}</Title>
          {questionSubheading && (
            <Subheading>
              <RichText document={questionSubheading} />
            </Subheading>
          )}
          {videoBlock && (
            <StyledLink to="#faq-video">
              <TextWithIcon type="video" icon={img}>
                {intl.formatMessage({ id: 'videoTutorial' })}
              </TextWithIcon>
            </StyledLink>
          )}
          <RichText document={answer} options={answerFormat} />
          {proTip && proTip.map(tip => <ProTip key={tip.id} data={tip} />)}
          {chart && <Chart data={chart} />}
        </Section>
      </StyledContainer>
    </>
  );
};

FAQAnswer.propTypes = {
  question: PropTypes.string.isRequired,
  questionSubheading: PropTypes.string,
  answer: PropTypes.shape(),
  category: PropTypes.string.isRequired,
  categorySlug: PropTypes.string.isRequired,
  proTip: PropTypes.arrayOf(PropTypes.shape()),
  videoBlock: PropTypes.arrayOf(PropTypes.shape()),
  chart: PropTypes.shape(),
};

export default FAQAnswer;
